import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import Icon from '../Icon/index'
import { makeIcon, IconName } from '../OracleCommons/icons'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'

export interface ContextMenuOption {
  id: string
  label: string
  iconName: string
  onClick: () => void
  variant?: 'default' | 'destructive'
}

interface OracleContextMenuProps {
  isVisible: boolean
  onClose: () => void
  options: ContextMenuOption[]
  position: { x: number; y: number } | null
  triggerElement?: HTMLElement | null
}

export const OracleContextMenu: React.FC<OracleContextMenuProps> = ({
  isVisible,
  onClose,
  options,
  position,
  triggerElement,
}) => {
  const menuRef = useRef<HTMLDivElement>(null)
  const [finalPosition, setFinalPosition] = React.useState<{ x: number; y: number } | null>(null)
  const [isPositioned, setIsPositioned] = React.useState(false)

  // Handle click outside to close menu
  useEffect(() => {
    if (!isVisible) {
      return undefined
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isVisible, onClose])

  // Handle escape key to close menu
  useEffect(() => {
    if (!isVisible) {
      return undefined
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [isVisible, onClose])

  // We have to prevent the menu from rendering offsecreen. This can happen if
  // the related menu item is near the bottom of the screen, or on mobile,
  // hanging off the right edge. To avoid this, render it with visibility:
  // hidden, and use its resulting position to determine where it 'should' be.
  useEffect(() => {
    if (!isVisible || !position || !menuRef.current) {
      setIsPositioned(false)
      setFinalPosition(null)
      return
    }

    // Measure the actual menu dimensions
    const menuRect = menuRef.current.getBoundingClientRect()
    const menuHeight = menuRect.height
    const menuWidth = menuRect.width

    // Get viewport dimensions
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const MARGIN = 8

    // Check if we're on a larger breakpoint (desktop with sidebar)
    const isDesktop = viewportWidth >= 768

    let finalX = position.x
    let finalY = position.y

    if (triggerElement) {
      const triggerRect = triggerElement.getBoundingClientRect()

      // Check vertical space
      const spaceBelow = viewportHeight - triggerRect.bottom
      const spaceAbove = triggerRect.top

      // Vertical positioning: prefer below, but flip if not enough space
      if (spaceBelow >= menuHeight + MARGIN) {
        // Enough space below - align with top of trigger
        finalY = triggerRect.top
      } else if (spaceAbove >= menuHeight + MARGIN) {
        // Not enough space below, but enough above - position above trigger
        finalY = triggerRect.bottom - menuHeight
      } else {
        // Not enough space either way - position at bottom with margin
        finalY = viewportHeight - menuHeight - MARGIN
      }
    }

    // Ensure menu stays within viewport bounds
    finalY = Math.max(MARGIN, Math.min(finalY, viewportHeight - menuHeight - MARGIN))

    if (isDesktop) {
      // Desktop: also check horizontal bounds
      finalX = Math.max(MARGIN, Math.min(finalX, viewportWidth - menuWidth - MARGIN))
    }

    setFinalPosition({ x: finalX, y: finalY })
    setIsPositioned(true)
  }, [isVisible, position, triggerElement])

  // Calculate menu position in viewport coordinates
  const getMenuPosition = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      position: 'fixed',
      zIndex: 10002, // Higher than mobile sidebar (10001)
    }

    if (!position) {
      return {
        ...baseStyles,
        left: '0px',
        top: '0px',
        opacity: 0,
      }
    }

    // If we haven't calculated final position yet, render hidden for measurement
    if (!isPositioned) {
      return {
        ...baseStyles,
        left: `${position.x}px`,
        top: `${position.y}px`,
        visibility: 'hidden', // render hidden to measure its render box wrt viewport
      }
    }

    // Use calculated final position
    const viewportWidth = window.innerWidth
    const isDesktop = viewportWidth >= 768

    if (isDesktop && finalPosition) {
      return {
        ...baseStyles,
        left: `${finalPosition.x}px`,
        top: `${finalPosition.y}px`,
        visibility: 'visible',
      }
    }

    // Mobile behavior: align to right edge
    if (finalPosition) {
      return {
        ...baseStyles,
        right: 0,
        top: `${finalPosition.y}px`,
        visibility: 'visible',
      }
    }

    // Fallback
    return baseStyles
  }

  const menuLabel = useTranslatedText({ keys: ['contextMenu', 'menu'] })
  const closeLabel = useTranslatedText({ keys: ['contextMenu', 'close'] })

  const handleOptionClick = (option: ContextMenuOption) => {
    option.onClick()
    onClose()
  }

  if (!isVisible) {
    return null
  }

  // Render context menu through a portal to document.body
  return createPortal(
    <div
      ref={menuRef}
      className="oracle-context-menu"
      style={getMenuPosition()}
      role="menu"
      aria-label={menuLabel}
    >
      <div className="oracle-context-menu__header">
        <button
          className="oracle-context-menu__close-button"
          onClick={onClose}
          aria-label={closeLabel}
          type="button"
        >
          <Icon color="currentColor" size={24}>
            {makeIcon('close')}
          </Icon>
        </button>
      </div>

      <div className="oracle-context-menu__options">
        {options.map((option) => (
          <button
            key={option.id}
            className={`oracle-context-menu__option oracle-context-menu__option--${option.variant || 'default'}`}
            onClick={() => handleOptionClick(option)}
            role="menuitem"
            type="button"
          >
            <Icon color="currentColor" size={24}>
              {makeIcon(option.iconName as IconName)}
            </Icon>
            <span className="oracle-context-menu__option-label">
              {option.label}
            </span>
          </button>
        ))}
      </div>
    </div>,
    document.body,
  )
}
